// Phase 7-G スケジュール画像のデータ変換層
// 仕様: membership_kyuko_spec_v5.md 2.2（校舎単位の集計ステータス）
//
// get_lesson_placements（配置マスター）+ get_schedule_overrides（休講情報）から
// 日別の entries { [day]: { badge, lines } } を構築する。
// ScheduleImageTemplate はこの entries を受け取って描画するだけ（色・レイアウト責務）。

const DOW_FIELD = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];
const DOW_JP = ["日", "月", "火", "水", "木", "金", "土"];

// 「一部○○」混在時にラベルへ採用する状態の優先度（仕様 2.2.1-3）
const PARTIAL_PRIORITY = ["closed", "reschedule", "substitute", "timeChange"];

// 全○○ / 一部○○ → バッジ kind（ScheduleImageTemplate の BADGE_STYLE キー）
const FULL_BADGE = {
  closed: "fullClosed",
  substitute: "fullSubstitute",
  reschedule: "fullReschedule",
  timeChange: "fullTimeChange",
};
const PARTIAL_BADGE = {
  closed: "partialClosed",
  substitute: "partialSubstitute",
  reschedule: "partialReschedule",
  timeChange: "partialTimeChange",
};

const pad = (n) => String(n).padStart(2, "0");

// 振替元日付 "2026-05-13" → "5/13(火)分の振替"（仕様 2.2.5）
function formatReschedule(dateStr) {
  if (!dateStr) return "振替";
  const [y, m, d] = dateStr.split("-").map(Number);
  const dow = new Date(y, m - 1, d).getDay();
  return `${m}/${d}(${DOW_JP[dow]})分の振替`;
}

// 振替先ラベル（振替元セルの休講注記用）。
// 時刻変更なし: "7/4(土)へ振替" / あり: "7/4(土) 19:00-20:00へ振替"
function formatRescheduleTo(dateStr, newStart, newEnd) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  const dow = new Date(y, m - 1, d).getDay();
  const time = newStart && newEnd ? ` ${newStart}-${newEnd}` : "";
  return `${m}/${d}(${DOW_JP[dow]})${time}へ振替`;
}

// クラス1件の表記 → 【HH:MM - HH:MM 講師名 レッスン名 対象】
// SchedulePage グリッド（name + " " + target）と表記を揃える。対象が空なら付けない。
// startOverride/endOverride は振替先で開催時刻が変わる場合に実際の時刻へ差し替える用。
function classLabel(p, startOverride, endOverride) {
  const target = p.content_target ? ` ${p.content_target}` : "";
  const s = startOverride || p.start_time;
  const e = endOverride || p.end_time;
  return `【${s} - ${e} ${p.instructor_name} ${p.content_name}${target}】`;
}

// クラス1件の行データ { text, note?, noteType?, remark? }（仕様 2.2.3 の状態併記）
// remark = 管理画面で入力した備考（Schedule.note）。休講の「備考」と時間変更の「振替割振の有無」。
function buildLine(p, status, ov) {
  const line = buildLineBase(p, status, ov);
  // 振替元（closed + reschedule_from_date）の note は SchedulePage の保存時に
  // 「M/D（曜）へ振替」が自動生成されて入る（手入力ではない）。
  // text 側に同じ内容を注記済みなので、ここで出すと二重になる。
  const isMoveOut = status === "closed" && ov?.reschedule_from_date;
  // 備考の入力欄は休講・時間変更のみ。他ステータスに切替後も残った note は出さない。
  const hasNoteField = status === "closed" || status === "timeChange";
  const remark = (hasNoteField && !isMoveOut && ov && ov.note) || "";
  return remark ? { ...line, remark } : line;
}

function buildLineBase(p, status, ov) {
  const base = classLabel(p);
  switch (status) {
    case "open":
      return { text: `${base}通常営業` };
    case "closed":
      // 振替元（新方式）は closed + reschedule_from_date(=振替先) で保存。
      // 休講に加え「→M/D(曜) HH:MM-HH:MMへ振替」を併記（時刻は変わる場合のみ）。
      return {
        text: base,
        note: ov?.reschedule_from_date
          ? `⚠休講 →${formatRescheduleTo(ov.reschedule_from_date, ov.new_start, ov.new_end)}`
          : "⚠休講",
        noteType: "red",
      };
    case "substitute":
      return {
        text: base,
        note: `※${ov?.substitute_instructor || ""}代講`,
        noteType: "green",
      };
    case "reschedule":
      return {
        text: base,
        note: formatReschedule(ov?.reschedule_from_date),
        noteType: "green",
      };
    case "timeChange":
      return {
        text: base,
        note: `→ ${ov?.new_start || ""}-${ov?.new_end || ""}`,
        noteType: "green",
      };
    default:
      return { text: base };
  }
}

/**
 * 日別 entries を構築する。
 *
 * @param {object} params
 * @param {number} params.year
 * @param {number} params.month - 1-12
 * @param {number} params.studioId - 対象校舎
 * @param {Array} params.placements - get_lesson_placements の placements（対象校舎分）
 * @param {object} params.overridesResponse - get_schedule_overrides のレスポンス（日付キー）
 * @returns {object} { [day:number]: { badge, lines } }
 */
export function buildScheduleEntries({
  year,
  month,
  studioId,
  placements = [],
  overridesResponse = {},
}) {
  const entries = {};
  const daysInMonth = new Date(year, month, 0).getDate();

  // 対象校舎の配置のみ。space_flg（空き枠）や時刻 null のダミーは除外。
  const studioPlacements = placements.filter(
    (p) =>
      p.studio_id === studioId &&
      !p.space_flg &&
      p.start_time &&
      p.content_name,
  );

  // 振替先マップ（新方式）: closed + reschedule_from_date(=振替先) を持つ override を
  // “振替先の日付”へ投影する。振替先には別レコードを持たず、元の closed から派生させる。
  // 形: { [destKey]: Array<{ placement_id, originKey }> }
  const incomingByDate = {};
  Object.entries(overridesResponse).forEach(([dKey, payload]) => {
    ((payload && payload.overrides) || []).forEach((o) => {
      if (o.status === "closed" && o.reschedule_from_date) {
        const dest = o.reschedule_from_date;
        if (!incomingByDate[dest]) incomingByDate[dest] = [];
        incomingByDate[dest].push({
          placement_id: o.placement_id,
          originKey: dKey,
          // 振替先で開催時刻が変わる場合の実開催時刻（無ければ元クラスの時刻を使う）
          newStart: o.new_start,
          newEnd: o.new_end,
        });
      }
    });
  });

  for (let day = 1; day <= daysInMonth; day++) {
    const dateKey = `${year}-${pad(month)}-${pad(day)}`;
    const dow = new Date(year, month - 1, day).getDay();
    const dowField = DOW_FIELD[dow];
    const payload = overridesResponse[dateKey];

    // 1. 全クラス休講（仕様 2.2.1-1）
    if (payload && payload.all_closed_studios !== undefined) {
      const ac = payload.all_closed_studios;
      const hit =
        ac === "all" || (Array.isArray(ac) && ac.includes(studioId));
      if (hit) {
        entries[day] = { badge: "fullClosed", lines: [] };
        continue;
      }
    }

    // その曜日のクラス（時刻昇順）
    const dayPlacements = studioPlacements
      .filter((p) => p.day_of_week === dowField)
      .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));

    // override を placement_id でマップ化
    const overrides = (payload && payload.overrides) || [];
    const ovByPid = {};
    overrides.forEach((o) => {
      ovByPid[o.placement_id] = o;
    });

    // 各クラスの状態
    const perClass = dayPlacements.map((p) => {
      const ov = ovByPid[p.id];
      return { p, status: ov ? ov.status : "open", ov };
    });
    const nonOpen = perClass.filter((c) => c.status !== "open");

    // 振替先の投入（他日 closed の reschedule_from_date が指すクラス）を行に変換。
    // 曜日が異なることが多く native の dayPlacements には現れないため、ここで別途生成する。
    // 同じ振替を旧方式（振替先日の reschedule）でも持っている場合は二重表示を避けて旧方式側を採用。
    const incoming = (incomingByDate[dateKey] || []).filter((inc) => {
      const ov = ovByPid[inc.placement_id];
      return !(ov && ov.status === "reschedule" && ov.reschedule_from_date === inc.originKey);
    });
    const incomingLines = incoming
      .map((inc) => {
        const p = studioPlacements.find((pp) => pp.id === inc.placement_id);
        return p
          ? {
              // 時刻が変わる振替は実際の開催時刻で表記する
              text: classLabel(p, inc.newStart, inc.newEnd),
              note: formatReschedule(inc.originKey),
              noteType: "green",
            }
          : null;
      })
      .filter(Boolean);

    // 4. 全クラス営業 かつ 振替先の投入も無し → バッジなし（仕様 2.2.1-4）
    if (nonOpen.length === 0 && incomingLines.length === 0) continue;

    const statusSet = new Set(nonOpen.map((c) => c.status));

    if (nonOpen.length === 0 && incomingLines.length > 0) {
      // 振替先（案A）：振替バッジ＋振替行のみ。その日の通常クラスは載せない。
      entries[day] = { badge: "fullReschedule", lines: incomingLines };
    } else if (
      incomingLines.length === 0 &&
      nonOpen.length === perClass.length &&
      statusSet.size === 1
    ) {
      // 2. 全○○（仕様 2.2.1-2）：Schedule 登録クラスのみ（仕様 2.2.4）
      const st = nonOpen[0].status;
      entries[day] = {
        badge: FULL_BADGE[st],
        lines: nonOpen.map((c) => buildLine(c.p, c.status, c.ov)),
      };
    } else {
      // 3. 一部○○（仕様 2.2.1-3）：その日の全クラスを併記＋振替先の投入行
      const kinds = new Set(statusSet);
      if (incomingLines.length > 0) kinds.add("reschedule");
      const labelStatus = PARTIAL_PRIORITY.find((s) => kinds.has(s));
      entries[day] = {
        badge: PARTIAL_BADGE[labelStatus],
        lines: [
          ...perClass.map((c) => buildLine(c.p, c.status, c.ov)),
          ...incomingLines,
        ],
      };
    }
  }

  return entries;
}
